import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { StateHistory } from '../../../data/models/stateHistory';
import { IValidationResults } from '../../../data/models/validation';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import { RestrictionSet } from '../Restrictions/RestrictionSet';
import RestrictionsTab from '../Restrictions/RestrictionsTab';
import GroupedProducts from './GroupedProducts/GroupedProducts';
import './ProductTabs.scss';
import StateHistoryTab from './StateHistory/StateHistoryTab';
import { useProductTabs } from '@/hooks/useProductTabs';
import { IAlbum } from '@/data/models/album';
import PriceTab from './PriceTab';
import { ICurrentChanges } from '@/types/product-current-changes-types';
import ChangeRequestCounter from '@/components/common/ChangeRequestCounter/ChangeRequestCounter';
import { IChangeRequest } from '@/data/models/changeRequest';
import {
  isValidationError,
  isValidationWarning,
} from '@/utils/validationErrors';
import { ProductViewModes } from '@/utils/productStateUtils';

interface Props {
  isValidFormat: boolean;
  restrictions?: RestrictionSet;
  stateHistory?: StateHistory[];
  toggleRestrictionsModal?: any;
  togglePricingModal?: any;
  album: IAlbum;
  readonly: boolean;
  errors: IValidationResults[];
  productViewMode?: ProductViewModes;
  currentChanges?: ICurrentChanges;
  changeRequest?: IChangeRequest | undefined;
}
function MerchProductTabs({
  restrictions,
  stateHistory,
  toggleRestrictionsModal,
  togglePricingModal,
  album,
  readonly,
  errors,
  productViewMode,
  currentChanges,
  changeRequest,
}: Props) {
  const { t } = useTranslation();
  const {
    tabIndex,
    setTabIndex,
    isValidRestrictions,
    showConfirmationModal,
    setShowConfirmationModal,
    confirmationModalBody,
    confirmationModalHeader,
    confirmationModalAction,
    groupedProducts,
  } = useProductTabs({
    album,
    errors,
  });

  const pricingIssues =
    errors?.filter((e) => e.fieldName === 'AlbumSalesTeams') ?? [];
  const hasPricingErrors = pricingIssues.some(isValidationError);
  const hasPricingWarnings =
    !hasPricingErrors && pricingIssues.some(isValidationWarning);
  const pricingTabClassName = hasPricingErrors
    ? 'has-error'
    : hasPricingWarnings
      ? 'has-warning'
      : '';
  const pricingIconClassName = hasPricingErrors
    ? 'icon fas fa-exclamation-triangle danger fa-lg'
    : hasPricingWarnings
      ? 'icon fas fa-exclamation-circle warning fa-lg'
      : 'icon far fa-tags fa-lg';
  const pricingTextClassName = hasPricingErrors
    ? 'item-text danger'
    : hasPricingWarnings
      ? 'item-text warning'
      : 'item-text';

  return (
    <div className="product-tabs">
      <ConfirmationModal
        showModal={showConfirmationModal}
        setShowModal={setShowConfirmationModal}
        message={confirmationModalBody}
        header={confirmationModalHeader}
        handleConfirm={confirmationModalAction}
      ></ConfirmationModal>
      <Tabs
        selectedIndex={tabIndex}
        onSelect={(index) => setTabIndex(index)}
        id="controlled-tabs"
        default
      >
        <TabList className="nav nav-tabs">
          <Tab className={isValidRestrictions ? '' : 'has-error'}>
            <a className="nav-tabs-product">
              <i
                className={
                  isValidRestrictions
                    ? 'icon fas fa-ban fa-lg'
                    : 'icon fas fa-exclamation-triangle danger fa-lg'
                }
              ></i>
              <span
                className={
                  isValidRestrictions ? 'item-text' : 'item-text danger'
                }
              >
                {t('product.restrictions.territories')}
              </span>
              <span className={'padding-left-x1'}>
                <ChangeRequestCounter
                  counter={currentChanges?.territoriesChanges.changesCounter}
                ></ChangeRequestCounter>
              </span>
            </a>
          </Tab>
          <Tab className={pricingTabClassName}>
            <a className="nav-tabs-pricing">
              <i className={pricingIconClassName}></i>
              <span className={pricingTextClassName}>
                {t('product.pricing')}
              </span>
              <span className={'padding-left-x1'}>
                <ChangeRequestCounter
                  counter={currentChanges?.pricingChanges.changesCounter}
                ></ChangeRequestCounter>
              </span>
            </a>
          </Tab>
          {stateHistory?.length &&
          productViewMode !== ProductViewModes.ChangeRequest &&
          productViewMode !== ProductViewModes.ChangeRequestReadOnly ? (
            <Tab className="">
              <a className="nav-tabs-product">
                <i className="icon far fa-history fa-lg"></i>
                <span className="item-text">{t('product.history')}</span>
              </a>
            </Tab>
          ) : null}
          {groupedProducts?.length ? (
            <Tab className="">
              <a className="nav-tabs-product">
                <i className="icon fas fa-link fa-lg"></i>
                <span className="item-text">
                  {t('product.grouped_products')}
                </span>
              </a>
            </Tab>
          ) : null}
        </TabList>
        <div className="tab-content">
          <TabPanel>
            {restrictions ? (
              <RestrictionsTab
                readonly={readonly}
                restrictions={restrictions}
                toggleRestrictionsModal={toggleRestrictionsModal}
              ></RestrictionsTab>
            ) : (
              <span>{t('generic.loading')}</span>
            )}
          </TabPanel>
          <TabPanel>
            <PriceTab
              album={album}
              readonly={readonly}
              togglePricingModal={togglePricingModal}
              errors={errors}
              changeRequest={changeRequest}
              currentChanges={currentChanges?.pricingChanges}
            ></PriceTab>
          </TabPanel>
          <TabPanel>
            {stateHistory?.length ? (
              <StateHistoryTab
                stateHistory={stateHistory}
                album={album}
              ></StateHistoryTab>
            ) : (
              <div></div>
            )}
          </TabPanel>
          {groupedProducts?.length ? (
            <TabPanel>
              <GroupedProducts albums={groupedProducts ?? []} />
            </TabPanel>
          ) : null}
        </div>
      </Tabs>
    </div>
  );
}

export default MerchProductTabs;
